"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { pick } from "@/lib/tool-kit/random-fun/rng";

const ARTICLES = ["the", "a"];

const ADJECTIVES = [
  "curious",
  "ancient",
  "quiet",
  "brave",
  "weary",
  "cheerful",
  "clever",
  "gentle",
  "mysterious",
  "patient",
  "rusty",
  "golden",
  "crooked",
  "narrow",
  "distant",
  "forgotten",
  "elegant",
  "humble",
  "restless",
  "shimmering",
];

const SUBJECT_NOUNS = [
  "scientist",
  "sailor",
  "teacher",
  "gardener",
  "musician",
  "librarian",
  "carpenter",
  "painter",
  "traveler",
  "farmer",
  "astronomer",
  "baker",
  "detective",
  "shepherd",
  "poet",
  "engineer",
  "historian",
  "fisherman",
  "innkeeper",
  "mapmaker",
];

const OBJECT_NOUNS = [
  "map",
  "letter",
  "lantern",
  "door",
  "journal",
  "telescope",
  "basket",
  "melody",
  "bridge",
  "garden",
  "recipe",
  "painting",
  "puzzle",
  "compass",
  "manuscript",
  "photograph",
  "violin",
  "clock",
  "sculpture",
  "ledger",
];

const VERBS = [
  "examined",
  "discovered",
  "repaired",
  "carried",
  "studied",
  "sketched",
  "admired",
  "followed",
  "collected",
  "described",
  "polished",
  "unfolded",
  "guarded",
  "restored",
  "borrowed",
  "photographed",
  "measured",
  "arranged",
  "unlocked",
  "remembered",
];

const ADVERBS = [
  "carefully",
  "quietly",
  "eagerly",
  "slowly",
  "proudly",
  "calmly",
  "secretly",
  "gracefully",
  "patiently",
  "boldly",
  "cheerfully",
  "reluctantly",
];

const PREPOSITIONAL_ENDINGS = [
  "near the harbor",
  "beside the old library",
  "under the fading light",
  "along the winding river",
  "behind the garden wall",
  "at the edge of town",
  "before the morning rain",
  "beneath the autumn sky",
  "outside the crowded market",
  "during the long afternoon",
];

const VOWEL_START = /^[aeiou]/i;

const MIN_COUNT = 1;
const MAX_COUNT = 20;

/** English strings. A translated content file overrides these through `ui`. */
export const RANDOM_SENTENCE_GENERATOR_STRINGS = {
  howManySentences: "How many sentences (1–20)",
  generateSentences: "Generate Sentences",
  copied: "Copied!",
  copyAll: "Copy All",
  yourRandomSentences: "Your random sentences",
} as const;

interface SentenceItem {
  id: string;
  text: string;
}

function indefiniteArticle(word: string): string {
  return VOWEL_START.test(word) ? "an" : "a";
}

function nounPhrase(nouns: string[]): string {
  const adjective = pick(ADJECTIVES);
  const noun = pick(nouns);
  const article = pick(ARTICLES) === "a" ? indefiniteArticle(adjective) : "the";
  return `${article} ${adjective} ${noun}`;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const TEMPLATES: (() => string)[] = [
  () =>
    `${capitalize(nounPhrase(SUBJECT_NOUNS))} ${pick(ADVERBS)} ${pick(VERBS)} ${nounPhrase(OBJECT_NOUNS)} ${pick(PREPOSITIONAL_ENDINGS)}.`,
  () =>
    `${capitalize(nounPhrase(SUBJECT_NOUNS))} ${pick(VERBS)} ${nounPhrase(OBJECT_NOUNS)} ${pick(PREPOSITIONAL_ENDINGS)}.`,
  () =>
    `${capitalize(pick(ADVERBS))}, ${nounPhrase(SUBJECT_NOUNS)} ${pick(VERBS)} ${nounPhrase(OBJECT_NOUNS)}.`,
  () =>
    `${capitalize(pick(PREPOSITIONAL_ENDINGS))}, ${nounPhrase(SUBJECT_NOUNS)} ${pick(ADVERBS)} ${pick(VERBS)} ${nounPhrase(OBJECT_NOUNS)}.`,
  () =>
    `${capitalize(nounPhrase(SUBJECT_NOUNS))} and ${nounPhrase(SUBJECT_NOUNS)} ${pick(VERBS)} ${nounPhrase(OBJECT_NOUNS)} together.`,
  () =>
    `${capitalize(nounPhrase(SUBJECT_NOUNS))} ${pick(VERBS)} ${nounPhrase(OBJECT_NOUNS)} and ${pick(ADVERBS)} ${pick(VERBS)} ${nounPhrase(OBJECT_NOUNS)}.`,
  () =>
    `While ${nounPhrase(SUBJECT_NOUNS)} waited ${pick(PREPOSITIONAL_ENDINGS)}, ${nounPhrase(SUBJECT_NOUNS)} ${pick(VERBS)} ${nounPhrase(OBJECT_NOUNS)}.`,
  () =>
    `${capitalize(nounPhrase(SUBJECT_NOUNS))} never ${pick(VERBS)} ${nounPhrase(OBJECT_NOUNS)} without pausing ${pick(PREPOSITIONAL_ENDINGS)}.`,
];

function generateSentence(): string {
  return pick(TEMPLATES)();
}

function clampCount(value: number): number {
  if (Number.isNaN(value)) {
    return MIN_COUNT;
  }
  return Math.min(MAX_COUNT, Math.max(MIN_COUNT, Math.floor(value)));
}

export default function RandomSentenceGeneratorWidget() {
  const strings = useToolUi(RANDOM_SENTENCE_GENERATOR_STRINGS);
  const [count, setCount] = useState(5);
  const [sentences, setSentences] = useState<SentenceItem[]>([]);
  const [copied, setCopied] = useState(false);

  function handleGenerate() {
    const items: SentenceItem[] = [];
    for (let i = 0; i < count; i++) {
      items.push({ id: crypto.randomUUID(), text: generateSentence() });
    }
    setSentences(items);
    setCopied(false);
  }

  function handleCopyAll() {
    if (sentences.length === 0) {
      return;
    }
    const text = sentences.map((s) => s.text).join("\n");
    writeToClipboard(text).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Count */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="sentence-count"
        >
          {strings.howManySentences}
        </label>
        <input
          className="w-32 rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          id="sentence-count"
          max={MAX_COUNT}
          min={MIN_COUNT}
          onChange={(e) => setCount(clampCount(Number(e.target.value)))}
          type="number"
          value={count}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
          onClick={handleGenerate}
          type="button"
        >
          {strings.generateSentences}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={sentences.length === 0}
          onClick={handleCopyAll}
          type="button"
        >
          {copied ? strings.copied : strings.copyAll}
        </button>
      </div>

      {/* Results */}
      {sentences.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-medium text-foreground text-sm">
            {strings.yourRandomSentences}
          </h2>
          <ul className="space-y-2">
            {sentences.map((s) => (
              <li
                className="rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed"
                key={s.id}
              >
                {s.text}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
